import { HttpClient } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { EnvironmentService } from '../environment/environment.service';
import { UserConsentCreate, UserConsentRead, CookieConsent } from '@core/models/user-consent.model';

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly STORAGE_KEY = 'd2d_cookie_consent';
  private readonly CONSENT_ID_KEY = 'd2d_consent_id';
  private readonly CURRENT_VERSION = '1.0';

  private consentSubject = new BehaviorSubject<CookieConsent | null>(null);
  public consent$ = this.consentSubject.asObservable();

  private readonly environment = inject(EnvironmentService);
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly API_URL = this.environment.getApiUrl();

  public createConsent(data: UserConsentCreate): Observable<UserConsentRead> {
    return this.http.post<UserConsentRead>(`${this.API_URL}v1/user-consents/`, data);
  }

  constructor() {
    this.loadConsent();
  }

  private loadConsent(): void {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        try {
          const consent: CookieConsent = JSON.parse(stored);
          // Check version if needed, for now just load it
          this.consentSubject.next(consent);
        } catch (e) {
          console.error('Error parsing cookie consent', e);
          localStorage.removeItem(this.STORAGE_KEY);
        }
      }
    }
  }

  public hasConsent(): boolean {
    return this.consentSubject.value !== null;
  }

  public getConsent(): CookieConsent | null {
    return this.consentSubject.value;
  }

  public saveConsentId(id: number): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.CONSENT_ID_KEY, String(id));
    }
  }

  public saveConsent(categories: { analytics: boolean; marketing: boolean }): void {
    if (isPlatformBrowser(this.platformId)) {
      const consent: CookieConsent = {
        version: this.CURRENT_VERSION,
        timestamp: new Date().toISOString(),
        categories: {
          essential: true, // Always true
          analytics: categories.analytics,
          marketing: categories.marketing,
        },
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(consent));
      this.consentSubject.next(consent);

      // Here you would typically trigger GTM/GA initialization if analytics is true
      if (consent.categories.analytics) {
        this.initializeAnalytics();
      }
    }
  }

  public getStoredConsentId(): number | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const id = localStorage.getItem(this.CONSENT_ID_KEY);
    return id ? Number(id) : null;
  }

  private initializeAnalytics(): void {
    // Placeholder for GTM/GA initialization logic
    // e.g. window.dataLayer.push({'event': 'cookie_consent_analytics_granted'});
    console.log('Analytics cookies accepted - Initializing tracking...'); // eslint-disable-line no-console
  }
}
